import { api } from "@lib/orpc-client";
import { useState, useEffect, useCallback } from "react";
import { SupportedBank } from "./useSupportedBank";
import {
  AddBankParams,
  EditBankParams,
  RedemptionOption,
} from "../types/redemption";
import * as Sentry from "@sentry/react";

type PaymentBankAccount = Awaited<
  ReturnType<typeof api.wallet.banking.listPayment>
>[number];

const useManagePaymentBank = (fetchBanks: boolean = true) => {
  const [bankAsRedemptionOption, setBankAsRedemptionOption] = useState<
    RedemptionOption[]
  >([]);
  const [supportedBanks, setSupportedBanks] = useState<SupportedBank[]>([]);
  const [banks, setBanks] = useState<PaymentBankAccount[]>([]);
  const [localLoading, setLocalLoading] = useState(fetchBanks);

  const getBankData = async () => {
    try {
      setLocalLoading(true);
      const response = await api.wallet.banking.listPayment({});
      const supportedBanks = await api.walletV2.banking.supportedBanks({});
      setSupportedBanks(supportedBanks);
      const bankAsRedemptionOption: RedemptionOption[] = response.map(
        (bank) => {
          return {
            id: bank.bank_uuid,
            name: bank.bank_account_name,
            iconUrl:
              supportedBanks?.find(
                (supportedBank) => supportedBank.code === bank.bank_name
              )?.icon_url || "",
            accountNumber: bank.bank_account,
            provider:
              supportedBanks?.find(
                (supportedBank) => supportedBank.code === bank.bank_name
              )?.display_name || "",
            nickname: bank.account_nickname,
            isValid: bank.status === "VALIDATED",
          };
        }
      );

      setBankAsRedemptionOption(bankAsRedemptionOption);
      setBanks(response);
      return response;
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setLocalLoading(false);
    }
  };

  const getData = useCallback(async () => {
    await getBankData();
  }, []);

  useEffect(() => {
    if (!fetchBanks) {
      setLocalLoading(false);
      return;
    }

    void getData().catch(() => undefined);
  }, [fetchBanks, getData]);

  const addBankPayment = async ({
    account_nickname,
    asset = "idr",
    source = "PAYMENT",
    bank_name,
    bank_account,
  }: AddBankParams) => {
    try {
      return await api.walletV2.banking.add({
        account_nickname,
        bank_name,
        bank_account,
        asset,
        source,
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const editBankPayment = async ({
    bank_uuid,
    account_nickname,
    asset = "idr",
    source = "PAYMENT",
    bank_name,
    bank_account,
  }: EditBankParams) => {
    try {
      return await api.walletV2.banking.update({
        bank_uuid,
        account_nickname,
        bank_name,
        bank_account,
        asset,
        source,
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const validateBankPayment = async ({ bank_uuid }: { bank_uuid: string }) => {
    try {
      return await api.walletV2.banking.validation({ bank_uuid });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const prepareBankPayment = async (
    params: AddBankParams & { bank_uuid?: string }
  ) => {
    try {
      setLocalLoading(true);
      let response;
      if (params.bank_uuid) {
        response = await editBankPayment(params as EditBankParams);
      } else {
        response = await addBankPayment(params as AddBankParams);
      }

      if (response.status === "VALIDATED") {
        return response;
      } else if (response.status === "VALIDATION_REQUESTED") {
        const secondResponse = await validateBankPayment({
          bank_uuid: response.bank_uuid,
        });
        if (secondResponse.status === "VALIDATED") {
          return secondResponse;
        } else if (secondResponse.status === "VALIDATION_REQUESTED") {
          throw secondResponse.status;
        } else {
          throw secondResponse.status;
        }
      } else {
        throw response.status;
      }
    } finally {
      setLocalLoading(false);
    }
  };

  const isLoading = localLoading;

  return {
    data: banks,
    isLoading,
    supportedBanks,
    bankAsRedemptionOption,
    prepareBankPayment,
  };
};

export default useManagePaymentBank;
